import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from 'react-i18next';
import styled, { useTheme } from 'styled-components';
import { Designer } from '@brg/api';
import { Colors, FInter, useBreakpoint, useToasts, ExternalLink } from '@brg/ui';
import { ProfileRole, ProfileUtils, useProfile } from '@brg/auth';
import { LayoutConst } from 'core/modules';
import { computeDesigner } from '../../helpers/computeData';
import { DesignerTab, PROFILE_TABS_IN_ORDER, PROFILE_TABS_ME_IN_ORDER } from '../../constants/designerTab';
import useQueryState from "../../hooks/useQueryState";
import { AppTester } from 'supporting';
import { useServiceLinks } from "../../hooks/useServiceLinks";
import { ADDITIONAL_TABS_SIGN } from './components/designerSliderItem';
import useProfileTabs from "../../hooks/useProfileTabs";
import { ProjectsGridActions, ProjectsGridSelector } from "../../reducers/ProjectsGridSlice";
import { useGetDesignerHandlers } from "./support/handlers";
import { useGetEmptyTabData } from 'shared/hooks/useGetEmptyTabData';
import { HsePolicies } from 'shared/constants/HsePolicies';
import { DesignerContent } from './designerContent';
import ProfileFoldersSlice, { ProfileFoldersProps, ProfileFoldersSelector } from 'shared/reducers/ProfileFoldersSlice';
import { useGetIsSubscribed } from 'shared/hooks/api/Subscribe/useGetIsSubscribed';


export const ServiceLink = styled(ExternalLink)`
  display: flex;
  align-items: center;
  color: ${Colors.galBlack47};
  cursor: pointer;
  transition: .2s all;
  color: ${(p) => (p.theme?.designerPage?.slideLineInActive || '')};

  &:hover {
    color: ${(p) => (p.theme?.designerPage?.slideLine || '')};
  }

  @media (max-width: ${LayoutConst.screen.l}px) {
    white-space: nowrap;
    span {
      font-size: 13px;
      line-height: 16px;
      white-space: nowrap;
    }
  }
`


export const Title = styled(FInter.SectionTitle)`
  line-height: 51px;
  font-size: 43px;

  @media (max-width: ${LayoutConst.screen.l}px) {
    font-size: 22px;
    line-height: 28px;
  }
  
  @media (max-width: ${LayoutConst.screen.s}px) {
    font-size: 22px;
    line-height: 28px;
  }
`
type PropsT = {
  whiteTheme?: boolean;
  ignoreContext?: boolean;
  contexts?: string[];
  designer?: Designer;
}

const DesignerPage: FC<PropsT> = ({ ignoreContext, contexts, whiteTheme, designer }) => {
  const { t } = useTranslation();
  const profile = useProfile();
  const theme = useTheme();
  const isMobile = useBreakpoint(LayoutConst.screen.m);
  const is744BP = useBreakpoint(LayoutConst.screen.s);
  const is743 = useBreakpoint(LayoutConst.screen.s - 1);
  const isMedia = AppTester.isMediiia();
  const isDafes = AppTester.isGeoDafes();
  const isCGRAVE = AppTester.isCGRAVE()
  const isSuperPuper = AppTester.isSuperPuper()
  const isGeo = (isCGRAVE || isSuperPuper || AppTester.isGeograffee()) && !isDafes;
  const isIqMediia = AppTester.isIqMediiia();
  const noProjectsRef = useRef<any>(null);
  const toasts = useToasts();
  const dispatch = useDispatch();
  const [emptyTabFilter] = useGetEmptyTabData();
  const isIfCIDStudent = !!designer?.globalTypes?.includes('Hsecreativestudent');
  const isLPUEmployee = !!designer?.globalTypes?.includes('lpuemployee');

  const isMe = useMemo(
    () => (profile && designer && designer.accountId === profile.accountId),
    [profile, designer]
  );

  // const {isSubscribed, isLoading} = useGetIsSubscribed(profile?.accountId || '', 0, designer && designer?.designerId ||  '', 'userProjects')
  const { isSubscribed, isLoading } = useGetIsSubscribed(designer && designer?.designerId || '', 0, profile?.accountId || '', '0')

  const hasEditProfilePolicy = useMemo(
    () => !!(profile && profile.rolesList && Array.isArray(profile.rolesList)
      && (new ProfileRole(HsePolicies.can_edit_designer_profile)).containsIn(profile.rolesList)),
    [profile]
  );

  const initialTab = isSuperPuper ? DesignerTab.games_archive : DesignerTab.author;
  const serviceLinks = useServiceLinks(designer?.designerId ?? '', designer);

  const storedSorting = localStorage.getItem('brg_sorting')
  const [{
    tab: queryTab,
    sorting: querySorting = storedSorting || 20,
    showcase = false,
    folderId = undefined
  }, setQueryParams] = useQueryState();
  const [tagId, setTagId] = useState('');
  const { accountId: id = '' } = designer || {};
  const [activeTabKey, setActiveTabKey] = useState<string>(initialTab);
  const [hasShowcaseProjects, setHasShowcaseProjects] = useState(false);
  const [tabDetermined, setTabDetermined] = useState(false);
  const { foldersList } = useSelector(ProfileFoldersSelector);
  const defaultTabInit = useSelector(ProfileFoldersSelector)?.defaultTabInit;
  const [addTab, setAddTab] = useState<string>(DesignerTab.workingMaterialsAuthor);
  const { currentFolder } = useSelector(ProfileFoldersSelector)

  const data = useSelector(ProjectsGridSelector)?.data.main;

  const defaultFolder = useMemo(
    () => (foldersList?.length > 0 ? foldersList?.find((i) => !!i?.isDefault) : undefined
    ), [foldersList])

  const draftProjects = data?.[DesignerTab.drafts]?.data;

  const isModer = useMemo(() => (ProfileUtils.isModer(profile) || ProfileUtils.isSuperModer(profile)), [profile])

  const shouldDisplayRoles = AppTester.isHSEDesign() || AppTester.isAcademy() || AppTester.isLPUDesign();
  const shouldDisplayStudentRole = !AppTester.isDeziiignMain()

  const isBooks = true;

  const {
    tabsFormatted: tabs,
    counts,
    tabsLoading: contentIsLoading,
    loadingError: isLoadingError,
    commonFilter,
    updateTabs,
  } = useProfileTabs(designer, tagId, showcase, activeTabKey, setHasShowcaseProjects);

  const context = `profile${id}`;

  const noProjects = useMemo(() => {
    if (!contentIsLoading) {
      const filteredCounts = Object.fromEntries(
        Object.entries(counts).filter(([key]) => key !== 'discount' && key !== 'about')
      );
      return {
        all: Object.values(filteredCounts).reduce((res: any, value: any) => {
          return res + value;
        }, 0) === 0,
        //@ts-ignore
        tab: counts[activeTabKey] === 0,
      };
    }
  }, [counts, contentIsLoading, activeTabKey])

  const isNoProjects = useMemo(() => (noProjects && !!noProjects.all), [noProjects])

  const tablist = useMemo(() => (isNoProjects && !isMe ? emptyTabFilter : tabs), [isNoProjects, isMe, tabs, emptyTabFilter])

  const shouldViewDefaultFolder = useMemo(() => {
    return defaultFolder &&
      (!folderId || folderId === '') &&
      !contentIsLoading &&
      !defaultTabInit &&
      activeTabKey === 'projects' &&
      data?.['projects']?.data?.length > 0
  }, [defaultFolder, folderId, contentIsLoading, defaultTabInit, activeTabKey, data]);

  const shouldVisibleSubscribeTab = useMemo(
    () => (activeTabKey === DesignerTab.subscribe)
    , [isMedia, activeTabKey])

  useEffect(() => {
    if (shouldViewDefaultFolder) {
      const isHidden = defaultFolder?.isHidden;
      if (!isHidden || isMe || hasEditProfilePolicy) {

        setQueryParams({ folderId: defaultFolder?.id })
        dispatch(ProfileFoldersSlice.actions.setCurrentFolder(ProfileFoldersProps.projects))
        dispatch(ProfileFoldersSlice.actions.setDefaultTabInit(true))
      }
    }
  }, [shouldViewDefaultFolder])

  useEffect(() => {
    if (!contentIsLoading) {
      dispatch(ProjectsGridActions.setTabs({
        tabs: tablist,
        categories: [],
        mainFilter: commonFilter,
        context,
      }))
    }
  }, [tablist, commonFilter, contentIsLoading])

  useEffect(() => {
    if (!noProjects) {
      noProjectsRef.current = true;
    }
  }, [noProjects])

  const sortFilter = useMemo(() => ({
    projectFilter: Number(querySorting),
    limit: 20,
  }), [querySorting, tagId]);

  const ParticipantOPA = useMemo(
    () =>
    (AppTester.isHSEDesign()
      && profile
      && designer
      && (isModer || profile?.globalTypes?.some((x: any) => x?.toLowerCase() === 'employee' || x?.toLowerCase() === 'cooperator'))
      && designer?.globalTypes?.some(x => x?.toLowerCase() === 'employee' || x?.toLowerCase() === 'cooperator'))
    , [designer, profile, isModer])

  const canViewDrafts = isMe || hasEditProfilePolicy;
  const canViewCompetitions = isMe || hasEditProfilePolicy;


  const computedDesigner = useMemo(() => computeDesigner(designer), [designer]);

  const contentStartedLoading = useRef(false);

  useEffect(() => {
    if (contentIsLoading) {
      contentStartedLoading.current = true;
    }
  }, [contentIsLoading, noProjects])

  useEffect(() => {
    if (!contentIsLoading && contentStartedLoading.current) {
      //@ts-ignore
      const firstNotEmpty = shouldVisibleSubscribeTab ? DesignerTab.subscribe : counts[activeTabKey] > 0 || (['projects', 'author', 'coauthor', 'students']?.includes(activeTabKey) && showcase) ? activeTabKey : [
        ...PROFILE_TABS_IN_ORDER,
        ...PROFILE_TABS_ME_IN_ORDER,
        //@ts-ignore
      ].find((item) => counts[item] > 0)

      const activeTab = (isMe || hasEditProfilePolicy) ? firstNotEmpty || (isSuperPuper ? DesignerTab.games_archive : DesignerTab.drafts) : firstNotEmpty || DesignerTab.author;
      setActiveTabKey(activeTab)
      if (activeTab === ADDITIONAL_TABS_SIGN) {
        //@ts-ignore
        setAddTab(counts[DesignerTab.workingMaterialsAuthor]
          //@ts-ignore
          ? DesignerTab.workingMaterialsAuthor : counts[DesignerTab.workingMaterialsCoauthor]
            ? DesignerTab.workingMaterialsCoauthor
            : DesignerTab.workingMaterialsStudents)
      }
      setTabDetermined(true);
    }
  }, [contentIsLoading, counts, shouldVisibleSubscribeTab, showcase])

  // список фильтров
  const filterList = useMemo(() => {
    return {
      //@ts-ignore
      authorTopProjectsFilter: tabs.find(({ path }) => path === DesignerTab.author)?.filter
    }
  }, [tabs])

  // Обработчики событий
  const onTabChange = (tab: string, addTab?: string) => {
    if (addTab && addTab !== '') {
      setAddTab(addTab);
      return;
    }
    if (tab === ADDITIONAL_TABS_SIGN) {
      //@ts-ignore
      setAddTab(counts[DesignerTab.workingMaterialsAuthor]
        //@ts-ignore
        ? DesignerTab.workingMaterialsAuthor : counts[DesignerTab.workingMaterialsCoauthor]
          ? DesignerTab.workingMaterialsCoauthor
          : DesignerTab.workingMaterialsStudents)
      setActiveTabKey(tab)
      return;
    }
    if (tab === DesignerTab.subscribe) {
      setAddTab(addTab ?? (counts[DesignerTab.subscriptions as keyof {}] > 0 ?
        DesignerTab.subscriptions : DesignerTab.subscribers))
    }
    setQueryParams({ showcase: false, folderId: undefined })
    setActiveTabKey(tab);
  };

  useEffect(() => {
    if (queryTab) {
      setActiveTabKey(queryTab as DesignerTab);
      if (ADDITIONAL_TABS_SIGN === queryTab) {
        //@ts-ignore
        setAddTab(counts[DesignerTab.workingMaterialsAuthor]
          //@ts-ignore
          ? DesignerTab.workingMaterialsAuthor : counts[DesignerTab.workingMaterialsCoauthor]
            ? DesignerTab.workingMaterialsCoauthor
            : DesignerTab.workingMaterialsStudents)
      }
      setQueryParams({ tab: null }, { replace: true })
    }
  }, [queryTab]);


  //todo разобраться с хэндлерами на странице дизайнера, по всей видимости они существуют в нескольких местах и перетираются
  const handlers = useGetDesignerHandlers(updateTabs, draftProjects, toasts, t)
  // const handlers = useMemo(() => designerHandlers(updateTabs, draftProjects, toasts, t), [updateTabs, draftProjects, t, toasts]);

  const onContextClick = (): void => {
    if (isLPUEmployee) {
      window.location.href = String(process.env.REACT_APP_URL_LPUDESIGN);
    } else if (isIfCIDStudent) {
      window.location.href = String(process.env.REACT_APP_URL_HSECREATIVE);
    } else {
      window.location.href = `${process.env.REACT_APP_URL_HSEDESIGN_COM}`;
    }

  };

  return <DesignerContent
    activeTabKey={activeTabKey}
    addTab={addTab}
    // buttons={buttons}
    canViewCompetitions={canViewCompetitions}
    canViewDrafts={canViewDrafts}
    computedDesigner={computedDesigner}
    contentIsLoading={contentIsLoading}
    counts={counts}
    designer={designer}
    filterList={filterList}
    handlers={handlers}
    hasEditProfilePolicy={hasEditProfilePolicy}
    is744BP={is744BP}
    is743={is743}
    isBooks={isBooks}
    isGeo={isGeo}
    isIqMediia={isIqMediia}
    isLoadingError={isLoadingError}
    isMobile={isMobile}
    ignoreContext={ignoreContext}
    isMe={isMe}
    isSubscribed={isSubscribed}
    isNoProjects={isNoProjects}
    noProjectsRef={noProjectsRef}
    noProjects={noProjects}
    onContextClick={onContextClick}
    onTabChange={onTabChange}
    ParticipantOPA={ParticipantOPA}
    profile={profile}
    serviceLinks={serviceLinks}
    setTagId={setTagId}
    shouldDisplayRoles={shouldDisplayRoles}
    shouldDisplayStudentRole={shouldDisplayStudentRole}
    sortFilter={sortFilter}
    tabDetermined={tabDetermined}
    tagId={tagId}
    theme={theme}
    toasts={toasts}
    whiteTheme={whiteTheme}
    hasShowcaseProjects={hasShowcaseProjects}
    currentFolder={currentFolder}
  />
};

export default DesignerPage;
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from 'react-i18next';
import styled, { useTheme } from 'styled-components';
import { Designer } from '@brg/api';
import { Colors, FInter, useBreakpoint, useToasts, ExternalLink } from '@brg/ui';
import { ProfileRole, ProfileUtils, useProfile } from '@brg/auth';
import { LayoutConst } from 'core/modules';
import { computeDesigner } from '../../helpers/computeData';
import { DesignerTab, PROFILE_TABS_IN_ORDER, PROFILE_TABS_ME_IN_ORDER } from '../../constants/designerTab';
import useQueryState from "../../hooks/useQueryState";
import { AppTester } from 'supporting';
import { useServiceLinks } from "../../hooks/useServiceLinks";
import { ADDITIONAL_TABS_SIGN } from './components/designerSliderItem';
import useProfileTabs from "../../hooks/useProfileTabs";
import { ProjectsGridActions, ProjectsGridSelector } from "../../reducers/ProjectsGridSlice";
import { useGetDesignerHandlers } from "./support/handlers";
import { useGetEmptyTabData } from 'shared/hooks/useGetEmptyTabData';
import { HsePolicies } from 'shared/constants/HsePolicies';
import { DesignerContent } from './designerContent';
import ProfileFoldersSlice, { ProfileFoldersProps, ProfileFoldersSelector } from 'shared/reducers/ProfileFoldersSlice';
import { useGetIsSubscribed } from 'shared/hooks/api/Subscribe/useGetIsSubscribed';


export const ServiceLink = styled(ExternalLink)`
  display: flex;
  align-items: center;
  color: ${Colors.galBlack47};
  cursor: pointer;
  transition: .2s all;
  color: ${(p) => (p.theme?.designerPage?.slideLineInActive || '')};

  &:hover {
    color: ${(p) => (p.theme?.designerPage?.slideLine || '')};
  }

  @media (max-width: ${LayoutConst.screen.l}px) {
    white-space: nowrap;
    span {
      font-size: 13px;
      line-height: 16px;
      white-space: nowrap;
    }
  }
`


export const Title = styled(FInter.SectionTitle)`
  line-height: 51px;
  font-size: 43px;

  @media (max-width: ${LayoutConst.screen.l}px) {
    font-size: 22px;
    line-height: 28px;
  }
  
  @media (max-width: ${LayoutConst.screen.s}px) {
    font-size: 22px;
    line-height: 28px;
  }
`
type PropsT = {
  whiteTheme?: boolean;
  ignoreContext?: boolean;
  contexts?: string[];
  designer?: Designer;
}

const DesignerPage: FC<PropsT> = ({ ignoreContext, contexts, whiteTheme, designer }) => {
  const { t } = useTranslation();
  const profile = useProfile();
  const theme = useTheme();
  const isMobile = useBreakpoint(LayoutConst.screen.m);
  const is744BP = useBreakpoint(LayoutConst.screen.s);
  const is743 = useBreakpoint(LayoutConst.screen.s - 1);
  const isMedia = AppTester.isMediiia();
  const isDafes = AppTester.isGeoDafes();
  const isCGRAVE = AppTester.isCGRAVE()
  const isSuperPuper = AppTester.isSuperPuper()
  const isGeo = (isCGRAVE || isSuperPuper || AppTester.isGeograffee()) && !isDafes;
  const isIqMediia = AppTester.isIqMediiia();
  const noProjectsRef = useRef<any>(null);
  const toasts = useToasts();
  const dispatch = useDispatch();
  const [emptyTabFilter] = useGetEmptyTabData();
  const isIfCIDStudent = !!designer?.globalTypes?.includes('Hsecreativestudent');
  const isLPUEmployee = !!designer?.globalTypes?.includes('lpuemployee');

  const isMe = useMemo(
    () => (profile && designer && designer.accountId === profile.accountId),
    [profile, designer]
  );

  // const {isSubscribed, isLoading} = useGetIsSubscribed(profile?.accountId || '', 0, designer && designer?.designerId ||  '', 'userProjects')
  const { isSubscribed, isLoading } = useGetIsSubscribed(designer && designer?.designerId || '', 0, profile?.accountId || '', '0')

  const hasEditProfilePolicy = useMemo(
    () => !!(profile && profile.rolesList && Array.isArray(profile.rolesList)
      && (new ProfileRole(HsePolicies.can_edit_designer_profile)).containsIn(profile.rolesList)),
    [profile]
  );

  const initialTab = isSuperPuper ? DesignerTab.games_archive : DesignerTab.author;
  const serviceLinks = useServiceLinks(designer?.designerId ?? '', designer);

  const storedSorting = localStorage.getItem('brg_sorting')
  const [{
    tab: queryTab,
    sorting: querySorting = storedSorting || 20,
    showcase = false,
    folderId = undefined
  }, setQueryParams] = useQueryState();
  const [tagId, setTagId] = useState('');
  const { accountId: id = '' } = designer || {};
  const [activeTabKey, setActiveTabKey] = useState<string>(initialTab);
  const [hasShowcaseProjects, setHasShowcaseProjects] = useState(false);
  const [tabDetermined, setTabDetermined] = useState(false);
  const { foldersList } = useSelector(ProfileFoldersSelector);
  const defaultTabInit = useSelector(ProfileFoldersSelector)?.defaultTabInit;
  const [addTab, setAddTab] = useState<string>(DesignerTab.workingMaterialsAuthor);
  const { currentFolder } = useSelector(ProfileFoldersSelector)

  const data = useSelector(ProjectsGridSelector)?.data.main;

  const defaultFolder = useMemo(
    () => (foldersList?.length > 0 ? foldersList?.find((i) => !!i?.isDefault) : undefined
    ), [foldersList])

  const draftProjects = data?.[DesignerTab.drafts]?.data;

  const isModer = useMemo(() => (ProfileUtils.isModer(profile) || ProfileUtils.isSuperModer(profile)), [profile])

  const shouldDisplayRoles = AppTester.isHSEDesign() || AppTester.isAcademy() || AppTester.isLPUDesign();
  const shouldDisplayStudentRole = !AppTester.isDeziiignMain()

  const isBooks = true;

  const {
    tabsFormatted: tabs,
    counts,
    tabsLoading: contentIsLoading,
    loadingError: isLoadingError,
    commonFilter,
    updateTabs,
  } = useProfileTabs(designer, tagId, showcase, activeTabKey, setHasShowcaseProjects);

  const context = `profile${id}`;

  const noProjects = useMemo(() => {
    if (!contentIsLoading) {
      const filteredCounts = Object.fromEntries(
        Object.entries(counts).filter(([key]) => key !== 'discount' && key !== 'about')
      );
      return {
        all: Object.values(filteredCounts).reduce((res: any, value: any) => {
          return res + value;
        }, 0) === 0,
        //@ts-ignore
        tab: counts[activeTabKey] === 0,
      };
    }
  }, [counts, contentIsLoading, activeTabKey])

  const isNoProjects = useMemo(() => (noProjects && !!noProjects.all), [noProjects])

  const tablist = useMemo(() => (isNoProjects && !isMe ? emptyTabFilter : tabs), [isNoProjects, isMe, tabs, emptyTabFilter])

  const shouldViewDefaultFolder = useMemo(() => {
    return defaultFolder &&
      (!folderId || folderId === '') &&
      !contentIsLoading &&
      !defaultTabInit &&
      activeTabKey === 'projects' &&
      data?.['projects']?.data?.length > 0
  }, [defaultFolder, folderId, contentIsLoading, defaultTabInit, activeTabKey, data]);

  const shouldVisibleSubscribeTab = useMemo(
    () => (activeTabKey === DesignerTab.subscribe)
    , [isMedia, activeTabKey])

  useEffect(() => {
    if (shouldViewDefaultFolder) {
      const isHidden = defaultFolder?.isHidden;
      if (!isHidden || isMe || hasEditProfilePolicy) {

        setQueryParams({ folderId: defaultFolder?.id })
        dispatch(ProfileFoldersSlice.actions.setCurrentFolder(ProfileFoldersProps.projects))
        dispatch(ProfileFoldersSlice.actions.setDefaultTabInit(true))
      }
    }
  }, [shouldViewDefaultFolder])

  useEffect(() => {
    if (!contentIsLoading) {
      dispatch(ProjectsGridActions.setTabs({
        tabs: tablist,
        categories: [],
        mainFilter: commonFilter,
        context,
      }))
    }
  }, [tablist, commonFilter, contentIsLoading])

  useEffect(() => {
    if (!noProjects) {
      noProjectsRef.current = true;
    }
  }, [noProjects])

  const sortFilter = useMemo(() => ({
    projectFilter: Number(querySorting),
    limit: 20,
  }), [querySorting, tagId]);

  const ParticipantOPA = useMemo(
    () =>
    (AppTester.isHSEDesign()
      && profile
      && designer
      && (isModer || profile?.globalTypes?.some((x: any) => x?.toLowerCase() === 'employee' || x?.toLowerCase() === 'cooperator'))
      && designer?.globalTypes?.some(x => x?.toLowerCase() === 'employee' || x?.toLowerCase() === 'cooperator'))
    , [designer, profile, isModer])

  const canViewDrafts = isMe || hasEditProfilePolicy;
  const canViewCompetitions = isMe || hasEditProfilePolicy;


  const computedDesigner = useMemo(() => computeDesigner(designer), [designer]);

  const contentStartedLoading = useRef(false);

  useEffect(() => {
    if (contentIsLoading) {
      contentStartedLoading.current = true;
    }
  }, [contentIsLoading, noProjects])

  useEffect(() => {
    if (!contentIsLoading && contentStartedLoading.current) {
      //@ts-ignore
      const firstNotEmpty = shouldVisibleSubscribeTab ? DesignerTab.subscribe : counts[activeTabKey] > 0 || (['projects', 'author', 'coauthor', 'students']?.includes(activeTabKey) && showcase) ? activeTabKey : [
        ...PROFILE_TABS_IN_ORDER,
        ...PROFILE_TABS_ME_IN_ORDER,
        //@ts-ignore
      ].find((item) => counts[item] > 0)

      const activeTab = (isMe || hasEditProfilePolicy) ? firstNotEmpty || (isSuperPuper ? DesignerTab.games_archive : DesignerTab.drafts) : firstNotEmpty || DesignerTab.author;
      setActiveTabKey(activeTab)
      if (activeTab === ADDITIONAL_TABS_SIGN) {
        //@ts-ignore
        setAddTab(counts[DesignerTab.workingMaterialsAuthor]
          //@ts-ignore
          ? DesignerTab.workingMaterialsAuthor : counts[DesignerTab.workingMaterialsCoauthor]
            ? DesignerTab.workingMaterialsCoauthor
            : DesignerTab.workingMaterialsStudents)
      }
      setTabDetermined(true);
    }
  }, [contentIsLoading, counts, shouldVisibleSubscribeTab, showcase])

  // список фильтров
  const filterList = useMemo(() => {
    return {
      //@ts-ignore
      authorTopProjectsFilter: tabs.find(({ path }) => path === DesignerTab.author)?.filter
    }
  }, [tabs])

  // Обработчики событий
  const onTabChange = (tab: string, addTab?: string) => {
    if (addTab && addTab !== '') {
      setAddTab(addTab);
      return;
    }
    if (tab === ADDITIONAL_TABS_SIGN) {
      //@ts-ignore
      setAddTab(counts[DesignerTab.workingMaterialsAuthor]
        //@ts-ignore
        ? DesignerTab.workingMaterialsAuthor : counts[DesignerTab.workingMaterialsCoauthor]
          ? DesignerTab.workingMaterialsCoauthor
          : DesignerTab.workingMaterialsStudents)
      setActiveTabKey(tab)
      return;
    }
    if (tab === DesignerTab.subscribe) {
      setAddTab(addTab ?? (counts[DesignerTab.subscriptions as keyof {}] > 0 ?
        DesignerTab.subscriptions : DesignerTab.subscribers))
    }
    setQueryParams({ showcase: false, folderId: undefined })
    setActiveTabKey(tab);
  };

  useEffect(() => {
    if (queryTab) {
      setActiveTabKey(queryTab as DesignerTab);
      if (ADDITIONAL_TABS_SIGN === queryTab) {
        //@ts-ignore
        setAddTab(counts[DesignerTab.workingMaterialsAuthor]
          //@ts-ignore
          ? DesignerTab.workingMaterialsAuthor : counts[DesignerTab.workingMaterialsCoauthor]
            ? DesignerTab.workingMaterialsCoauthor
            : DesignerTab.workingMaterialsStudents)
      }
      setQueryParams({ tab: null }, { replace: true })
    }
  }, [queryTab]);


  //todo разобраться с хэндлерами на странице дизайнера, по всей видимости они существуют в нескольких местах и перетираются
  const handlers = useGetDesignerHandlers(updateTabs, draftProjects, toasts, t)
  // const handlers = useMemo(() => designerHandlers(updateTabs, draftProjects, toasts, t), [updateTabs, draftProjects, t, toasts]);

  const onContextClick = (): void => {
    if (isLPUEmployee) {
      window.location.href = String(process.env.REACT_APP_URL_LPUDESIGN);
    } else if (isIfCIDStudent) {
      window.location.href = String(process.env.REACT_APP_URL_HSECREATIVE);
    } else {
      window.location.href = `${process.env.REACT_APP_URL_HSEDESIGN_COM}`;
    }

  };

  return <DesignerContent
    activeTabKey={activeTabKey}
    addTab={addTab}
    // buttons={buttons}
    canViewCompetitions={canViewCompetitions}
    canViewDrafts={canViewDrafts}
    computedDesigner={computedDesigner}
    contentIsLoading={contentIsLoading}
    counts={counts}
    designer={designer}
    filterList={filterList}
    handlers={handlers}
    hasEditProfilePolicy={hasEditProfilePolicy}
    is744BP={is744BP}
    is743={is743}
    isBooks={isBooks}
    isGeo={isGeo}
    isIqMediia={isIqMediia}
    isLoadingError={isLoadingError}
    isMobile={isMobile}
    ignoreContext={ignoreContext}
    isMe={isMe}
    isSubscribed={isSubscribed}
    isNoProjects={isNoProjects}
    noProjectsRef={noProjectsRef}
    noProjects={noProjects}
    onContextClick={onContextClick}
    onTabChange={onTabChange}
    ParticipantOPA={ParticipantOPA}
    profile={profile}
    serviceLinks={serviceLinks}
    setTagId={setTagId}
    shouldDisplayRoles={shouldDisplayRoles}
    shouldDisplayStudentRole={shouldDisplayStudentRole}
    sortFilter={sortFilter}
    tabDetermined={tabDetermined}
    tagId={tagId}
    theme={theme}
    toasts={toasts}
    whiteTheme={whiteTheme}
    hasShowcaseProjects={hasShowcaseProjects}
    currentFolder={currentFolder}
  />
};

export default DesignerPage;
